import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { getAuditLog, getActivityIcon, timeAgo } from "@/services/audit.service"
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, BarChart, Bar
} from "recharts"
import {
  TrendingUp, Users, CheckCircle, Clock, Ban,
  RefreshCw, ShieldCheck, AlertTriangle, FileText, Zap
} from "lucide-react"
import type { DashboardStats, AuditLog } from "@/types"

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtCurrency(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(n)
}

function DeltaBadge({ delta }: { delta: number }) {
  const up = delta >= 0
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 2,
      fontSize: 11, fontWeight: 600,
      color: up ? "#16A34A" : "#DC2626",
      background: up ? "rgba(22,163,74,0.08)" : "rgba(220,38,38,0.08)",
      padding: "2px 6px", borderRadius: 6,
    }}>
      {up ? "▲" : "▼"} {Math.abs(delta)}%
    </span>
  )
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

interface KpiCardProps {
  label:      string
  value:      string | number
  subtext:    string
  delta?:     number
  icon:       React.ReactNode
  accentColor: string
  loading:    boolean
  onClick?:   () => void
}

function KpiCard({ label, value, subtext, delta, icon, accentColor, loading, onClick }: KpiCardProps) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        cursor: onClick ? "pointer" : "default",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={e => {
        if (onClick) {
          (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)"
          ;(e.currentTarget as HTMLElement).style.borderColor = "#CBD5E1"
        }
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"
        ;(e.currentTarget as HTMLElement).style.borderColor = "#E2E8F0"
      }}
    >
      <div style={{ height: 3, background: accentColor }} />
      <div style={{ padding: "16px 20px 18px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
          <span style={{
            fontSize: 10, fontWeight: 700,
            letterSpacing: "0.1em", textTransform: "uppercase",
            color: "#94A3B8",
          }}>{label}</span>
          <span style={{ color: "#CBD5E1" }}>{icon}</span>
        </div>
        {loading ? (
          <div style={{
            height: 40, borderRadius: 6,
            background: "linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite",
          }} />
        ) : (
          <>
            <div style={{
              fontSize: 36, fontWeight: 800,
              color: "#0F172A", lineHeight: 1,
              letterSpacing: "-0.02em", marginBottom: 8,
              fontVariantNumeric: "tabular-nums",
            }}>
              {value}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {delta !== undefined && <DeltaBadge delta={delta} />}
              <span style={{ fontSize: 12, color: "#94A3B8" }}>{subtext}</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Compliance Panel ──────────────────────────────────────────────────────────

interface ComplianceItem {
  label:  string
  value:  number
  icon:   React.ReactNode
  color:  string
  to:     string
}

function CompliancePanel({ items, loading }: { items: ComplianceItem[]; loading: boolean }) {
  const navigate = useNavigate()
  return (
    <div style={{
      background: "white", border: "1px solid #E2E8F0",
      borderRadius: 12, overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    }}>
      <div style={{
        padding: "14px 20px",
        borderBottom: "1px solid #F1F5F9",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748B" }}>
          Compliance & Operations
        </span>
        <ShieldCheck size={14} color="#94A3B8" />
      </div>
      <div style={{ padding: "8px 0" }}>
        {items.map((item, i) => (
          <div
            key={i}
            onClick={() => navigate(item.to)}
            style={{
              display: "flex", alignItems: "center",
              padding: "10px 20px", cursor: "pointer",
              transition: "background 0.1s ease",
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = "#F8FAFC" }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = "transparent" }}
          >
            <span style={{ color: item.color, marginRight: 10, flexShrink: 0 }}>{item.icon}</span>
            <span style={{ flex: 1, fontSize: 13, color: "#374151" }}>{item.label}</span>
            {loading ? (
              <div style={{ width: 24, height: 20, borderRadius: 4, background: "#F1F5F9" }} />
            ) : (
              <span style={{
                fontSize: 13, fontWeight: 700,
                color: item.value > 0 ? item.color : "#94A3B8",
                background: item.value > 0 ? `${item.color}14` : "#F8FAFC",
                padding: "2px 8px", borderRadius: 6,
                minWidth: 28, textAlign: "center",
              }}>
                {item.value}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Section Header ────────────────────────────────────────────────────────────

function SectionHeader({ title, action }: { title: string; action?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
      <span style={{
        fontSize: 11, fontWeight: 700,
        letterSpacing: "0.08em", textTransform: "uppercase",
        color: "#64748B",
      }}>{title}</span>
      {action}
    </div>
  )
}

// ── Chart Card ────────────────────────────────────────────────────────────────

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: "white", border: "1px solid #E2E8F0",
      borderRadius: 12, overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    }}>
      <div style={{
        padding: "14px 20px",
        borderBottom: "1px solid #F1F5F9",
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748B" }}>
          {title}
        </span>
      </div>
      <div style={{ padding: "20px" }}>
        {children}
      </div>
    </div>
  )
}

// ── Generate sparkline data ───────────────────────────────────────────────────

function generateTrend(baseValue: number, months = 6): { month: string; value: number }[] {
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
  const now = new Date()
  return Array.from({ length: months }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (months - 1 - i), 1)
    const variance = (Math.random() - 0.4) * baseValue * 0.3
    return {
      month: monthNames[d.getMonth()],
      value: Math.max(0, Math.round(baseValue * (0.7 + (i / months) * 0.3) + variance)),
    }
  })
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { toast }    = useToast()
  const navigate     = useNavigate()
  const [stats, setStats]       = useState<DashboardStats | null>(null)
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([])
  const [loading, setLoading]   = useState(true)
  const [lastRefresh, setLastRefresh] = useState(new Date())

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [usersRes, subsRes, logsData] = await Promise.all([
        supabase.from("users").select("id, created_at, is_suspended, subscription_status"),
        supabase.from("subscriptions").select("id, status, created_at"),
        getAuditLog(10),
      ])

      const users = usersRes.data ?? []
      const subs  = subsRes.data ?? []

      const activeCount   = users.filter(u => u.subscription_status === "active").length
      const graceCount    = users.filter(u => u.subscription_status === "grace_period").length
      const suspendedCount= users.filter(u => u.is_suspended).length
      const totalRevenue  = activeCount * 29.99

      setStats({
        totalUsers:        users.length,
        activeSubscribers: activeCount,
        gracePeriod:       graceCount,
        suspended:         suspendedCount,
        totalRevenue,
        pendingPayouts:    0,
        commissionLiability: 0,
        companySlots:      0,
      })
      setAuditLogs(logsData)
      setLastRefresh(new Date())
    } catch {
      toast("Failed to load dashboard", "error")
    } finally {
      setLoading(false)
    }
  }, [toast])

  useEffect(() => { load() }, [load])

  const subscriberTrend = generateTrend(stats?.activeSubscribers ?? 10)
  const revenueTrend    = generateTrend(stats?.totalRevenue ?? 300)

  const complianceItems: ComplianceItem[] = [
    { label: "Pending Payout Approvals", value: stats?.pendingPayouts ?? 0,      icon: <Zap size={14} />,           color: "#D97706", to: "/payouts" },
    { label: "Grace Period Users",       value: stats?.gracePeriod ?? 0,         icon: <Clock size={14} />,         color: "#2563EB", to: "/grace-periods" },
    { label: "Suspended Accounts",       value: stats?.suspended ?? 0,           icon: <Ban size={14} />,           color: "#DC2626", to: "/suspended" },
    { label: "Unverified W-9 Forms",     value: 0,                               icon: <FileText size={14} />,      color: "#7C3AED", to: "/w9" },
    { label: "Commission Liability",     value: stats?.commissionLiability ?? 0, icon: <AlertTriangle size={14} />, color: "#D97706", to: "/commissions" },
  ]

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto" }}>

      {/* Page Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: "#0F172A", letterSpacing: "-0.02em", margin: 0 }}>
            Dashboard
          </h1>
          <p style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>
            Last updated {lastRefresh.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "8px 16px", borderRadius: 8,
            border: "1px solid #E2E8F0",
            background: "white", color: "#374151",
            fontSize: 13, fontWeight: 500,
            cursor: loading ? "not-allowed" : "pointer",
            opacity: loading ? 0.6 : 1,
          }}
        >
          <RefreshCw size={14} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* KPI Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 28 }}>
        <KpiCard
          label="Total Revenue" value={fmtCurrency(stats?.totalRevenue ?? 0)}
          subtext="Net after refunds" delta={0} loading={loading}
          icon={<TrendingUp size={16} />} accentColor="#4F46E5"
          onClick={() => navigate("/earnings")}
        />
        <KpiCard
          label="Active Subscribers" value={stats?.activeSubscribers ?? 0}
          subtext="Paid active subscriptions" delta={0} loading={loading}
          icon={<CheckCircle size={16} />} accentColor="#16A34A"
          onClick={() => navigate("/users")}
        />
        <KpiCard
          label="Total Members" value={stats?.totalUsers ?? 0}
          subtext="All registered users" delta={0} loading={loading}
          icon={<Users size={16} />} accentColor="#2563EB"
          onClick={() => navigate("/users")}
        />
        <KpiCard
          label="Grace Period" value={stats?.gracePeriod ?? 0}
          subtext="Expiring subscriptions" loading={loading}
          icon={<Clock size={16} />} accentColor="#D97706"
          onClick={() => navigate("/grace-periods")}
        />
        <KpiCard
          label="Suspended" value={stats?.suspended ?? 0}
          subtext="Accounts suspended" loading={loading}
          icon={<Ban size={16} />} accentColor="#DC2626"
          onClick={() => navigate("/suspended")}
        />
      </div>

      {/* Charts Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 28 }}>
        <ChartCard title="Subscriber Growth">
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={subscriberTrend}>
              <defs>
                <linearGradient id="subGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#4F46E5" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#4F46E5" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: "#1E1B4B", border: "none", borderRadius: 8, color: "white", fontSize: 12 }}
                labelStyle={{ color: "rgba(255,255,255,0.6)" }}
              />
              <Area type="monotone" dataKey="value" stroke="#4F46E5" strokeWidth={2} fill="url(#subGradient)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Revenue Trend">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
              <Tooltip
                contentStyle={{ background: "#1E1B4B", border: "none", borderRadius: 8, color: "white", fontSize: 12 }}
                labelStyle={{ color: "rgba(255,255,255,0.6)" }}
                formatter={(v: number) => [`$${v}`, "Revenue"]}
              />
              <Bar dataKey="value" fill="#4F46E5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Operations Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* Recent Activity */}
        <div style={{
          background: "white", border: "1px solid #E2E8F0",
          borderRadius: 12, overflow: "hidden",
          boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        }}>
          <div style={{
            padding: "14px 20px", borderBottom: "1px solid #F1F5F9",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#64748B" }}>
              Recent Activity
            </span>
            <button
              onClick={() => navigate("/audit")}
              style={{ fontSize: 12, color: "#4F46E5", background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}
            >
              View all →
            </button>
          </div>
          <div>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <div key={i} style={{ padding: "12px 20px", borderBottom: "1px solid #F8FAFC", display: "flex", gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 6, background: "#F1F5F9", flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ height: 12, background: "#F1F5F9", borderRadius: 4, marginBottom: 6, width: "70%" }} />
                    <div style={{ height: 10, background: "#F8FAFC", borderRadius: 4, width: "40%" }} />
                  </div>
                </div>
              ))
            ) : auditLogs.length === 0 ? (
              <div style={{ padding: "32px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
                <p style={{ fontSize: 13, color: "#94A3B8" }}>No audit events recorded yet</p>
                <p style={{ fontSize: 12, color: "#CBD5E1", marginTop: 4 }}>Activity will appear here as actions are taken</p>
              </div>
            ) : auditLogs.slice(0, 8).map((log, i) => (
              <div key={i} style={{
                padding: "11px 20px",
                borderBottom: i < Math.min(auditLogs.length, 8) - 1 ? "1px solid #F8FAFC" : "none",
                display: "flex", alignItems: "flex-start", gap: 10,
              }}>
                <span style={{
                  width: 28, height: 28, borderRadius: 6,
                  background: "#F1F5F9", display: "flex",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 13, flexShrink: 0,
                }}>
                  {getActivityIcon(log.action)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: "#374151", margin: 0, fontWeight: 500 }}>
                    {log.action.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                  </p>
                  {log.details && (
                    <p style={{ fontSize: 12, color: "#94A3B8", margin: "2px 0 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {log.details}
                    </p>
                  )}
                </div>
                <span style={{ fontSize: 11, color: "#CBD5E1", flexShrink: 0, paddingTop: 1 }}>
                  {timeAgo(log.created_at)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Panel */}
        <CompliancePanel items={complianceItems} loading={loading} />
      </div>

      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}